import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Theme toggle — sun ↔ moon ↔ auto.
 *
 * Three states cycled on click:
 *   "auto" → follows OS prefers-color-scheme   (no data-theme attr)
 *   "light" → forces light                     (data-theme="light")
 *   "dark"  → forces dark                      (data-theme="dark")
 *
 * Choice persists in localStorage. On first mount we restore the prior
 * choice and apply it to <html>; the CSS in pp-theme.css does the rest
 * via [data-theme] selectors.
 */

const STORAGE_KEY = 'pp_theme';

function readStored() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'auto') return v;
  } catch { /* SSR / disabled storage */ }
  return 'auto';
}

function apply(mode) {
  const el = document.documentElement;
  if (mode === 'auto') {
    el.removeAttribute('data-theme');
  } else {
    el.setAttribute('data-theme', mode);
  }
}

// Apply stored preference as early as possible — call this from index.js
// before React mounts so the first paint already has the right theme.
export function bootstrapTheme() {
  apply(readStored());
}

const NEXT = { auto: 'light', light: 'dark', dark: 'auto' };
// Auto-mode used pi-desktop which read as a "Screens" navigation icon in
// the navbar — confusing users into thinking it would open /screens.
// pi-palette is unambiguously a theme-picker symbol.
const ICON = { auto: 'pi pi-palette', light: 'pi pi-sun', dark: 'pi pi-moon' };
const LABEL = {
  auto:  'Theme: auto (follow system)',
  light: 'Theme: light',
  dark:  'Theme: dark',
};

export default function ThemeToggle() {
  const { t } = useTranslation();
  const [mode, setMode] = useState(readStored);

  // Apply on mount + whenever mode changes.
  useEffect(() => {
    apply(mode);
    try { localStorage.setItem(STORAGE_KEY, mode); } catch { /* ignore */ }
  }, [mode]);

  const cycle = () => setMode((m) => NEXT[m] ?? 'auto');

  return (
    <button
      type="button"
      className="pp-theme-toggle"
      onClick={cycle}
      aria-label={t(`theme.${mode}`, { defaultValue: LABEL[mode] })}
      title={t(`theme.${mode}`, { defaultValue: LABEL[mode] })}
    >
      <i className={ICON[mode]} />
    </button>
  );
}
